import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useSiteSettings } from './SiteSettingsContext';
import { sameSku } from '../services/shopCatalog';

/** What a basket line sells. A variant group never goes into the basket — only one of its variants. */
export type BasketItemKind = 'product' | 'variant' | 'bundle';

export interface BasketItem {
  /** The line's key (compared ignoring case): a product's, a variant's or a bundle's SKU. */
  sku: string;
  /** For a variant, the composed "Group – Label" name. */
  name: string;
  price: number;
  imageUrl?: string;
  quantity: number;
  /** A variant's own label ("6-0"). Absent on lines stored before variants existed. */
  variantLabel?: string;
  /** Absent on lines stored before variants and bundles existed; read as 'product'. */
  kind?: BasketItemKind;
}

interface BasketContextType {
  items: BasketItem[];
  itemCount: number;
  total: number;
  addItem: (item: Omit<BasketItem, 'quantity'>, quantity?: number) => void;
  removeItem: (sku: string) => void;
  updateQuantity: (sku: string, quantity: number) => void;
  clearBasket: () => void;
}

const BasketContext = createContext<BasketContextType | undefined>(undefined);

const getStorageKey = (siteNodeId: string | null): string =>
  `basket_${siteNodeId ?? 'default'}`;

/** Accepts lines stored by any version of the shop; drops anything that could not be checked out. */
const isStoredBasketItem = (value: unknown): value is BasketItem => {
  if (!value || typeof value !== 'object') return false;
  const line = value as Record<string, unknown>;
  return (
    typeof line.sku === 'string' &&
    line.sku.trim() !== '' &&
    typeof line.name === 'string' &&
    typeof line.price === 'number' &&
    Number.isFinite(line.price) &&
    typeof line.quantity === 'number' &&
    Number.isInteger(line.quantity) &&
    line.quantity > 0
  );
};

const loadBasket = (siteNodeId: string | null): BasketItem[] => {
  try {
    const raw = localStorage.getItem(getStorageKey(siteNodeId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isStoredBasketItem) : [];
  } catch {
    return [];
  }
};

const saveBasket = (siteNodeId: string | null, items: BasketItem[]): void => {
  try {
    if (items.length === 0) {
      localStorage.removeItem(getStorageKey(siteNodeId));
    } else {
      localStorage.setItem(getStorageKey(siteNodeId), JSON.stringify(items));
    }
  } catch {
    // localStorage full or unavailable — silent fail
  }
};

export const BasketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { settings } = useSiteSettings();
  const [items, setItems] = useState<BasketItem[]>(() => loadBasket(settings.siteNodeId));

  // Re-load basket when siteNodeId changes (e.g. on initial load)
  useEffect(() => {
    setItems(loadBasket(settings.siteNodeId));
  }, [settings.siteNodeId]);

  // Persist to localStorage on change
  useEffect(() => {
    saveBasket(settings.siteNodeId, items);
  }, [items, settings.siteNodeId]);

  const addItem = useCallback((item: Omit<BasketItem, 'quantity'>, quantity = 1) => {
    if (!Number.isInteger(quantity) || quantity <= 0) return;
    setItems(prev => {
      const existing = prev.find(i => sameSku(i.sku, item.sku));
      if (existing) {
        // Same SKU: one line. Refresh name/price/label from the latest catalogue data, keep the stored key.
        return prev.map(i =>
          i === existing
            ? { ...i, ...item, sku: i.sku, quantity: i.quantity + quantity }
            : i
        );
      }
      return [...prev, { ...item, quantity }];
    });
  }, []);

  const removeItem = useCallback((sku: string) => {
    setItems(prev => prev.filter(i => !sameSku(i.sku, sku)));
  }, []);

  const updateQuantity = useCallback((sku: string, quantity: number) => {
    if (quantity <= 0) {
      setItems(prev => prev.filter(i => !sameSku(i.sku, sku)));
    } else {
      setItems(prev =>
        prev.map(i => (sameSku(i.sku, sku) ? { ...i, quantity } : i))
      );
    }
  }, []);

  const clearBasket = useCallback(() => {
    setItems([]);
  }, []);

  const itemCount = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items]);
  const total = useMemo(() => items.reduce((sum, i) => sum + i.price * i.quantity, 0), [items]);

  const value = useMemo<BasketContextType>(
    () => ({ items, itemCount, total, addItem, removeItem, updateQuantity, clearBasket }),
    [items, itemCount, total, addItem, removeItem, updateQuantity, clearBasket]
  );

  return <BasketContext.Provider value={value}>{children}</BasketContext.Provider>;
};

export const useBasket = (): BasketContextType => {
  const context = useContext(BasketContext);
  if (!context) {
    throw new Error('useBasket must be used within a BasketProvider');
  }
  return context;
};
